import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  CatalogProduct,
  Cursors,
  ScheduledImport,
  SiteItem,
} from "@vviinn/user-managment-client-ts";

import { SiteTable } from "@vviinn/user-managment-client-ts/dist/models/SiteTable";

import { FetchableState, fetchCategories, scheduleSiteFeed } from "..";
import { ApiRequest, serializeModel } from "../../../api";
import { fetchSuccinctSites } from "../sites/sites-slice";
import { changeActiveSite } from "../site/site-slice";

import { categoryToAutosuggestion } from "../../../product/helpers/categoryToAutosuggestion";
import { RelevantActivatableCampaings } from "../../../campaign/types";
import { fetchCampaignTypeExist } from "../campaigns/fetch";
import {
  FetchProductsRequest,
  GetCatalogDetailsRequest,
  ProductRequest,
  SetProductAttributesRequest,
  TextSearchProductsRequest,
} from "../../../product/list/types";

export type ProductsState = FetchableState & {
  list: CatalogProduct[];
  cursors?: Cursors;
  scheduledImport: ScheduledImport;
  filterId: string;
  isFiltering: boolean;
};

type CatalogDetailsState = FetchableState & {
  productsCount: number;
  productsReady: number;
  imagesCount: number;
  imagesReady: number;
};

type ActiveCampaigns = {
  [p in RelevantActivatableCampaings]?: boolean;
};

export type ProductsSliceState = {
  products: ProductsState;
  catalogDetails: CatalogDetailsState;
  siteId?: SiteItem["id"];
  activeCampaignTypes: ActiveCampaigns;
  autoSuggestions?: { [key: string]: string[] };
};

const products: ProductsState = {
  list: [],
  cursors: undefined,
  filterId: "",
  scheduledImport: {
    feedUrl: "",
    isActive: false,
    triggerTimestamp: "",
  },
  isFiltering: false,
  loading: false,
  error: false,
  status: "idle",
};

const catalogDetails: CatalogDetailsState = {
  productsCount: 0,
  productsReady: 0,
  imagesCount: 0,
  imagesReady: 0,
  loading: false,
  error: false,
  status: "idle",
};

const initialState: ProductsSliceState = {
  products,
  catalogDetails,
  siteId: undefined,
  activeCampaignTypes: {},
  autoSuggestions: undefined,
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.pending, (state) => {
      state.products.error = false;
      state.products.loading = true;
      state.products.status = "loading";
    });
    builder.addCase(fetchProducts.rejected, (state) => {
      state.products.list = initialState.products.list;
      state.products.scheduledImport = initialState.products.scheduledImport;
      state.products.error = true;
      state.products.loading = false;
      state.products.status = "error";
    });
    builder.addCase(fetchProducts.fulfilled, (state, { payload }) => {
      if (payload.siteId !== state.siteId) return;

      state.products.list = payload.products;
      state.products.scheduledImport =
        payload.scheduledImport ?? initialState.products.scheduledImport;
      state.products.cursors = payload.cursors ?? initialState.products.cursors;
      state.products.filterId =
        payload.filterId ?? initialState.products.filterId;
      state.products.isFiltering =
        payload.filter !== undefined && payload.filter.length > 0;
      state.products.error = false;
      state.products.loading = false;
      state.products.status = "fulfilled";
    });

    builder.addCase(textSearchProducts.pending, (state) => {
      state.products.error = false;
      state.products.loading = true;
      state.products.status = "loading";
    });
    builder.addCase(textSearchProducts.rejected, (state) => {
      state.products.list = initialState.products.list;
      state.products.error = true;
      state.products.loading = false;
      state.products.status = "error";
    });
    builder.addCase(textSearchProducts.fulfilled, (state, { payload }) => {
      state.products.list = payload.products;
      state.products.error = false;
      state.products.loading = false;
      state.products.status = "fulfilled";
      state.products.isFiltering = true;
    });

    builder.addCase(fetchSuccinctSites.fulfilled, (state, { payload }) => {
      if (payload.length === 0) {
        state.products.list = initialState.products.list;
        state.products.scheduledImport = initialState.products.scheduledImport;
        state.products.cursors = initialState.products.cursors;
        state.products.filterId = initialState.products.filterId;
      }
    });
    builder.addCase(fetchSuccinctSites.rejected, (state) => {
      state.products.list = initialState.products.list;
      state.products.scheduledImport = initialState.products.scheduledImport;
      state.products.cursors = initialState.products.cursors;
      state.products.filterId = initialState.products.filterId;
    });

    builder.addCase(fetchCatalogDetails.pending, (state) => {
      state.catalogDetails.error = false;
      state.catalogDetails.loading = true;
      state.catalogDetails.status = "loading";
    });
    builder.addCase(fetchCatalogDetails.fulfilled, (state, { payload }) => {
      if (payload.siteId !== state.siteId) return;

      state.catalogDetails.error = false;
      state.catalogDetails.loading = false;
      state.catalogDetails.status = "fulfilled";

      state.catalogDetails.imagesCount = payload.imagesCount;
      state.catalogDetails.productsCount = payload.productsCount;
      state.catalogDetails.productsReady = payload.productsReady;
      state.catalogDetails.imagesReady = payload.imagesReady;
    });
    builder.addCase(deleteProductViaPlatform.fulfilled, (state, { meta }) => {
      const { productCode } = meta.arg.value;
      if (!productCode) return;
      state.products.list = state.products.list.filter(
        ({ id }) => id !== productCode,
      );
      state.catalogDetails.productsReady -= 1;
      state.catalogDetails.productsCount -= 1;
    });
    builder.addCase(fetchCatalogDetails.rejected, (state) => {
      state.catalogDetails.error = true;
      state.catalogDetails.loading = false;
      state.catalogDetails.status = "error";
    });
    builder.addCase(changeActiveSite, (state, { payload }) => {
      if (state.siteId === payload.id) return;

      const newState = {
        ...initialState,
        siteId: payload.id,
      };
      return newState;
    });
    builder.addCase(fetchCampaignTypeExist.fulfilled, (state, { payload }) => {
      if (payload) {
        state.activeCampaignTypes[payload] = true;
      }
    });
    builder.addCase(fetchCampaignTypeExist.rejected, (state, { meta }) => {
      const campaignType = meta.arg.value.campaignType;
      if (state.activeCampaignTypes[campaignType]) {
        state.activeCampaignTypes[campaignType] = false;
      }
    });
    builder.addCase(
      fetchAutosuggestions.fulfilled,
      (state, { payload, meta }) => {
        if (!state.autoSuggestions) state.autoSuggestions = {};
        const argKey = meta.arg.value.tableColumn;
        state.autoSuggestions[argKey] = payload;
      },
    );
    builder.addCase(fetchAutosuggestions.rejected, (state, { meta }) => {
      if (!state.autoSuggestions) state.autoSuggestions = {};
      const argKey = meta.arg.value.tableColumn;
      state.autoSuggestions[argKey] = [];
    });
    builder.addCase(fetchCategories.fulfilled, (state, { payload }) => {
      if (!state.autoSuggestions) state.autoSuggestions = {};
      state.autoSuggestions["product_type"] = categoryToAutosuggestion(
        payload.productTypeCategory,
      );
      state.autoSuggestions["google_product_category"] =
        categoryToAutosuggestion(payload.googleFeedCategory);
    });
    builder.addCase(fetchCategories.rejected, (state) => {
      if (!state.autoSuggestions) state.autoSuggestions = {};
      state.autoSuggestions["product_type"] = [];
      state.autoSuggestions["google_product_category"] = [];
    });
    builder.addCase(scheduleSiteFeed.fulfilled, (state, { payload }) => {
      state.products.scheduledImport = {
        ...payload,
        triggerTimestamp: payload.triggerTimestamp ?? "",
      };
    });
  },
});

export const fetchProducts = createAsyncThunk(
  "products/fetch",
  async ({ api, value }: ApiRequest<FetchProductsRequest>) => {
    const { companyId, siteId, cursor, filter, limit, sort } = value;
    if (!companyId) return { products: [] };
    const response = await api.getSiteProducts(
      companyId,
      siteId,
      cursor,
      limit,
      filter,
      undefined,
      sort,
    );

    return {
      products: response.products.map(serializeModel),
      filter,
      scheduledImport: serializeModel(response.scheduledImport),
      cursors: serializeModel(response.cursors),
      filterId: response.filterId,
      siteId,
    };
  },
);

export const fetchProductAnalyze = createAsyncThunk(
  "products/fetchAnalyze",
  async ({ api, value }: ApiRequest<ProductRequest>) => {
    const { companyId, siteId, productCode } = value;

    const response = await api.getProductAnalysis(
      companyId,
      siteId,
      productCode,
    );
    return serializeModel(response.value);
  },
);

export const fetchProductDetails = createAsyncThunk(
  "products/getCatalogProduct",
  async ({ api, value }: ApiRequest<ProductRequest>) => {
    const { companyId, siteId, productCode } = value;

    const response = await api.getCatalogProduct(
      companyId,
      siteId,
      productCode,
    );

    return serializeModel(response.value);
  },
);

export const deleteProductViaPlatform = createAsyncThunk(
  "products/fetch/deleteProductViaPlatform",
  async ({ api, value }: ApiRequest<ProductRequest>) => {
    const { companyId, siteId, productCode } = value;

    const response = await api.deleteProductViaPlatform(
      companyId,
      siteId,
      productCode,
    );
    return serializeModel(response.value);
  },
);

export const fetchCatalogDetails = createAsyncThunk(
  "feed/fetchCatalogDetails",
  async ({ api, value }: ApiRequest<GetCatalogDetailsRequest>) => {
    const { companyId, siteId } = value;
    const response = await api.getCatalogDetails(companyId, siteId);
    return serializeModel(response.value);
  },
);

const handleColorSuggestions = (suggestions: string[]) => [
  ...new Set(suggestions.map((item) => item.split("/")).flat()),
];

export const fetchAutosuggestions = createAsyncThunk(
  "site/autocomplete",
  async ({
    api,
    value,
  }: ApiRequest<{
    companyId: string;
    siteId: number;
    siteTable: SiteTable;
    tableColumn: string;
  }>) => {
    const { siteId, companyId, siteTable, tableColumn } = value;
    const response = await api.autocompleteSiteTableColumn(
      companyId,
      siteId,
      siteTable,
      tableColumn,
    );
    const suggestions = serializeModel(response.values);
    const confirmedSuggestions = serializeModel(response.confirmedValues);
    const relevantConfirmedSuggestions = (
      tableColumn === "color"
        ? handleColorSuggestions(confirmedSuggestions)
        : confirmedSuggestions
    ).sort((a, b) => a.localeCompare(b));
    const confirmedSuggestionsSet = new Set(relevantConfirmedSuggestions);
    const relevantActualSuggestions = (
      tableColumn === "color"
        ? handleColorSuggestions(suggestions)
        : suggestions
    )
      .filter((suggestion) => !confirmedSuggestionsSet.has(suggestion))
      .sort((a, b) => a.localeCompare(b));
    return [...relevantConfirmedSuggestions, ...relevantActualSuggestions];
  },
);

export const setProductImageAttributes = createAsyncThunk(
  "products/setAttributes",
  async ({ api, value }: ApiRequest<SetProductAttributesRequest>) => {
    const { companyId, siteId, productCode, ProductAnalysisValue } = value;

    const response = await api.setProductImageAttributes(
      companyId,
      siteId,
      productCode,
      ProductAnalysisValue,
    );
    return serializeModel(response.value);
  },
);

export const textSearchProducts = createAsyncThunk(
  "products/textSearch",
  async ({ api, value }: ApiRequest<TextSearchProductsRequest>) => {
    const { companyId, siteId, textSearchQuery } = value;

    if (!companyId || !siteId) return { products: [] };

    const response = await api.textSearchProducts(
      companyId,
      siteId,
      textSearchQuery,
    );

    return {
      products: response.suggestions.map(serializeModel),
      textSearchQuery,
    };
  },
);

export default productsSlice.reducer;
